import CleverTap from 'clevertap-web-sdk'
import { User } from '~/data/user/user'
import { PersonaFinance, PersonaInfo, PersonaResidence } from '~/data/persona'
import { Gender } from '~/data/common'
import {
  JbAffiliateOfferRedirectEvent,
  JbAffiliateOfferSelectedEvent,
  JbButtonClickEvent,
  JbClevertapEvent,
  JBClevertapEventData,
  JBClevertapProfileData,
  JbLoanSimulationEvent,
  JbModalDisplayedEvent,
  JbPageViewEvent,
  JbUserCreatedEvent,
  JbUserLoggedInEvent,
} from '~/data/clevertap/jb-clevertap'
import JbOAuth from '~/helpers/oauth'
import { waitForAResultWithOptions } from '~/helpers/offers'

CleverTap.init(import.meta.env.VITE_CLEVER_TAP_PROJECT_ID, 'us1')
CleverTap.spa = true

if (import.meta.env.DEV) {
  CleverTap.setLogLevel(3)
}

const initialState = () => ({
  serviceWorkerLoaded: false,
  serviceWorkerRegistering: false,
  serviceWorkerRegistration: null,
})

const state = initialState

// Helper function to map gender enum to CleverTap format
const mapGenderToCleverTapFormat = (gender?: Gender): 'M' | 'F' | undefined => {
  if (gender === Gender.MALE) return 'M'
  if (gender === Gender.FEMALE) return 'F'
  return undefined
}

const mapPhoneToCleverTapFormat = (phone: string): string => {
  return `+55${phone.replace(/\D/g, '')}`
}

// Helper function to get acquisition source from session storage
const getAcquisitionSource = (): string | null => {
  return (
    sessionStorage.getItem('utm_campaign') ||
    sessionStorage.getItem('utm_source')
  )
}
const getters = {
  isServiceWorkerLoaded: (state) => state.serviceWorkerLoaded,
}
const mutations = {
  SET_SERVICE_WORKER_LOADED(state, value) {
    state.serviceWorkerLoaded = value
  },
  SET_SERVICE_WORKER_REGISTERING(state, value) {
    state.serviceWorkerRegistering = value
  },
  SET_SERVICE_WORKER_REGISTRATION(state, registration) {
    state.serviceWorkerRegistration = registration
  },
}

interface ProfileChange {
  user: User
  persona?: {
    info?: PersonaInfo
    residence?: PersonaResidence
    finance?: PersonaFinance
  }
  score?: number
}

function createProfileDataRequest({
  user,
  persona,
  score,
}: ProfileChange): JBClevertapProfileData | null {
  if (!user.id) return null
  const profileData: JBClevertapProfileData = {
    Site: {
      Identity: user.id,
      Name: user.name,
      Phone: mapPhoneToCleverTapFormat(user.mobile_phone),
      Email: user.email,
      Gender: mapGenderToCleverTapFormat(persona?.info?.gender),
      DOB: persona?.info?.birth_date
        ? new Date(persona?.info.birth_date)
        : undefined,
      'MSG-email': true,
      'MSG-whatsapp': true,
      'MSG-sms': true,
      CreationSource: 'JB',
      AcquisitionSource: getAcquisitionSource(),
      StateOfResidence: persona?.residence?.state,
      CityOfResidence: persona?.residence?.city,
      IncomeSource: persona?.finance?.income_source,
      EducationLevel: persona?.info?.educational_level,
      IsAnnotated: persona?.finance?.annotated,
      MonthlyIncome: persona?.finance?.monthly_income,
      QuodScore: score,
    },
  }

  return Object.keys(profileData.Site).reduce(
    (acc, key) => {
      if (profileData.Site[key] !== undefined) {
        acc.Site[key] = profileData.Site[key]
      }
      return acc
    },
    { Site: {} } as JBClevertapProfileData,
  )
}

const actions = {
  async ensureServiceWorkerLoaded({ dispatch, getters }) {
    if (getters.isServiceWorkerLoaded) {
      console.log('CleverTap Service Worker is already loaded')
      return true
    }

    console.log('Waiting for CleverTap Service Worker to load...')

    try {
      const result = await waitForAResultWithOptions(
        () => getters.isServiceWorkerLoaded,
        async () => {
          try {
            await dispatch('actRegisterServiceWorker')
            return { data: true, status: 200 }
          } catch (error) {
            console.error('Falha ao registrar o Service Worker:', error)
            return { data: false, status: 500 }
          }
        },
        2000,
        5,
      )

      return result === true
    } catch (error) {
      if ((error as Error).message === 'MAX_RETRIES_REACHED') {
        console.error(
          'Max retries reached. Aborting Service Worker registration.',
        )
      } else {
        console.error('Error when waiting for CleverTap Service Worker:', error)
      }
      return false
    }
  },

  async actRegisterServiceWorker({ commit, state }) {
    if (state.serviceWorkerLoaded && state.serviceWorkerRegistration) {
      console.log('CleverTap Service Worker is already registered')
      return Promise.resolve(state.serviceWorkerRegistration)
    }

    if (state.serviceWorkerRegistering) {
      console.log('CleverTap Service Worker is being registered, waiting...')
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (state.serviceWorkerLoaded && state.serviceWorkerRegistration) {
            clearInterval(checkInterval)
            resolve(state.serviceWorkerRegistration)
          } else if (!state.serviceWorkerRegistering) {
            clearInterval(checkInterval)
            reject(new Error('Failed to register CleverTap Service Worker'))
          }
        }, 100)
      })
    }

    if ('serviceWorker' in navigator) {
      commit('SET_SERVICE_WORKER_REGISTERING', true)

      try {
        const registration =
          await navigator.serviceWorker.register('/clevertap-sw.js')
        console.log('CleverTap Service Worker registered')
        commit('SET_SERVICE_WORKER_REGISTRATION', registration)
        commit('SET_SERVICE_WORKER_LOADED', true)
        commit('SET_SERVICE_WORKER_REGISTERING', false)
        return registration
      } catch (e) {
        console.error('Failed to register CleverTap Service Worker:', e)
        commit('SET_SERVICE_WORKER_LOADED', false)
        commit('SET_SERVICE_WORKER_REGISTERING', false)
        return Promise.reject(e)
      }
    } else {
      console.warn('Service Worker is not supported in this browser.')
      commit('SET_SERVICE_WORKER_LOADED', false)
      return Promise.resolve(null)
    }
  },
  async actCleverTapAskForWebPush({ dispatch }) {
    console.log(
      'Checking if CleverTap Service Worker is loaded before requesting notifications.',
    )
    const serviceWorkerReady = await dispatch('ensureServiceWorkerLoaded')

    if (!serviceWorkerReady) {
      console.error(
        'Failed to register Clevertap Service Worker. Unable to request notifications',
      )
      return
    }

    console.log(
      'CleverTap Service Worker loaded, proceeding with notification request.',
    )

    CleverTap.notifications.push({
      titleText: 'Você gostaria de receber notificações?',
      bodyText:
        'Prometemos enviar apenas conteúdo relevante e atualizações sobre suas transações',
      okButtonText: 'Sim, quero!',
      rejectButtonText: 'Não, obrigado',
      okButtonColor: '#5420A4',
      askAgainTimeInSeconds: 5,
      skipDialog: false,
      serviceWorkerPath: '/clevertap-sw.js',
    })
  },
  actCleverTapOnUserLogin({ rootGetters, dispatch }) {
    if (!JbOAuth.isAccessTokenValid()) return
    const profileData = createProfileDataRequest({
      user: rootGetters['user/user/gtUser'],
      persona: rootGetters['user/persona/personaData'],
      score:
        rootGetters['products/credit-score/credit-score/gtCreditScoreLatest']
          ?.data?.score,
    })
    if (!profileData) return
    try {
      CleverTap.onUserLogin.push(profileData)
      dispatch('actCleverTapPushEvent', {
        eventName: JbClevertapEvent.USER_LOGGED_IN,
        eventData: {
          utm_source: sessionStorage.getItem('utm_source'),
          utm_medium: sessionStorage.getItem('utm_medium'),
          utm_campaign: sessionStorage.getItem('utm_campaign'),
          utm_term: sessionStorage.getItem('utm_term'),
          utm_product: sessionStorage.getItem('product') || 'login',
          Platform: navigator.userAgent,
        },
      } as JbUserLoggedInEvent)
    } catch (e) {
      console.log(e)
    }
  },
  actCleverTapProfilePush({ rootGetters }) {
    const profileData = createProfileDataRequest({
      user: rootGetters['user/user/gtUser'],
      persona: rootGetters['user/persona/personaData'],
      score:
        rootGetters['products/credit-score/credit-score/gtCreditScoreLatest']
          ?.data?.score,
    })
    if (!profileData) return
    try {
      CleverTap.profile.push(profileData)
    } catch (e) {}
  },
  actCleverTapPushEvent(
    _,
    pushData:
      | JbUserCreatedEvent
      | JbUserLoggedInEvent
      | JbLoanSimulationEvent
      | JbAffiliateOfferSelectedEvent
      | JbAffiliateOfferRedirectEvent
      | JbButtonClickEvent
      | JbModalDisplayedEvent
      | JbPageViewEvent
      | JBClevertapEventData,
  ) {
    try {
      if (pushData.eventData) {
        CleverTap.event.push(pushData.eventName, pushData.eventData)
      } else {
        CleverTap.event.push(pushData.eventName)
      }
    } catch (e) {}
  },
  actCleverTapOnUserLogout() {
    sessionStorage.removeItem('jb.state.clevertap.user-logged-in')
  },
}

export default { namespaced: true, state, getters, actions, mutations }
